import { enumNumericValues } from '@sonar-software/sonar-ts-lib';
import { FieldUsageTag } from './FieldUsageTag';
import { SonarSelector } from '../SonarSelector';

import { FieldUsageId, patchSalesforceSonarObject } from '@sonar-software/sonar-core';
import {
	genericProcessingFailure,
	ProcessingResult,
	useOptimisticUpdate,
	usePrevious,
	useProcessing,
	useSonarObjectReference,
	useUpdateSonarObjectReference
} from '@hooks';
import React, { useCallback, useEffect, useState } from 'react';

async function updateFieldUsage(sonarObjectId: number, fieldUsageId: FieldUsageId): Promise<ProcessingResult> {
	const result = await patchSalesforceSonarObject({
		fieldUsageId,
		sonarObjectId
	});

	return result.data ?? genericProcessingFailure;
}

const usageOptions = enumNumericValues(FieldUsageId)
	.map((value) => ({
		label: <FieldUsageTag fieldUsageId={ value } />,
		value: value
	}));

export interface FieldUsageSelectorProps {
	getPopupContainer?: (trigger: HTMLElement) => HTMLElement;
	isSpreadsheet?: boolean;
	sonarObjectId: number;
}

export const FieldUsageSelector: React.FC<FieldUsageSelectorProps> = ({
	getPopupContainer,
	isSpreadsheet,
	sonarObjectId
}) => {
	const { salesforce } = useSonarObjectReference(sonarObjectId);
	const { fieldUsageId } = salesforce;

	const [ value, setValue ] = useState(fieldUsageId);
	const { update } = useUpdateSonarObjectReference();
	const { start, state, stop } = useProcessing();
	const { changes } = useOptimisticUpdate(sonarObjectId);
	const previousFieldUsage = usePrevious(fieldUsageId);

	useEffect(() => {
		if (fieldUsageId !== previousFieldUsage) {
			setValue(fieldUsageId);
		}
	}, [ fieldUsageId, previousFieldUsage, value ]);

	const handleOnChange = useCallback(
		async(change: FieldUsageId) => {
			const newValue = change ?? FieldUsageId.None;

			if (fieldUsageId === newValue) {
				return;
			}

			const fallbackValue = value;
			setValue(newValue);

			start();
			const updateResult = await updateFieldUsage(sonarObjectId, newValue);

			if (updateResult?.success) {
				update({
					salesforce: { fieldUsageId: newValue },
					sonarObjectId
				});
			} else {
				setValue(fallbackValue);
			}

			stop(updateResult);
		},
		[ fieldUsageId, sonarObjectId, start, stop, update, value ]
	);

	return (
		<SonarSelector
			allowClear={ fieldUsageId !== FieldUsageId.None }
			boxless={ !isSpreadsheet }
			className='salesforce-field-usage-selector'
			defaultValue={ fieldUsageId }
			getPopupContainer={ getPopupContainer }
			isSalesforce
			isSpreadsheet={ isSpreadsheet }
			labelRender={ (option) => <FieldUsageTag fieldUsageId={ option.value as FieldUsageId } /> }
			onChange={ handleOnChange }
			options={ usageOptions }
			placeholder={ <FieldUsageTag fieldUsageId={ FieldUsageId.None } /> }
			readOnlyDisplay={ <FieldUsageTag fieldUsageId={ fieldUsageId } /> }
			showArrow
			showSearch={ false }
			state={ changes.fieldUsageId ? { isProcessing: true } : state }
			value={ value } />
	);
};
